import { HttpErrorResponse } from '@angular/common/http';
import type { MistakeType, RequestMistake } from '@workflows/types';
import type {
  MistakesFormPrefill,
  MistakesFormSelection,
} from './mistakes-form.types';

/**
 * Разложить текущие ошибки заявки по списку типов диалога. Ошибки с типами,
 * которых нет в списке (другой контрагент/архивный тип), сохраняются как есть,
 * чтобы сохранение диалога их не удалило.
 */
export function prefillMistakesForm(
  types: readonly MistakeType[],
  mistakes: readonly RequestMistake[],
): MistakesFormPrefill {
  const byType = new Map(
    mistakes.map(mistake => [mistake.mistakeTypeId, mistake]),
  );

  return {
    selection: types.map(type => {
      const mistake = byType.get(type.id);

      return {
        selected: Boolean(mistake),
        comment: mistake?.comment ?? null,
      };
    }),
    preserved: mistakes.filter(
      mistake => !types.some(type => type.id === mistake.mistakeTypeId),
    ),
  };
}

/** Ошибки для сохранения: отмеченные в диалоге и сохранённые неизвестные типы */
export function buildMistakesPayload(
  types: readonly MistakeType[],
  selection: readonly MistakesFormSelection[],
  preserved: readonly RequestMistake[],
): { mistakeTypeId: number; comment: string }[] {
  const selected = selection.flatMap((item, index) =>
    item.selected && types[index]
      ? [{ mistakeTypeId: types[index].id, comment: item.comment || '' }]
      : [],
  );

  return [
    ...selected,
    ...preserved.map(mistake => ({
      mistakeTypeId: mistake.mistakeTypeId,
      comment: mistake.comment || '',
    })),
  ];
}

/** Причина ошибки запроса для сообщения пользователю */
export function getMistakesErrorMessage(error: unknown): string | undefined {
  if (error instanceof HttpErrorResponse) {
    const body: unknown = error.error;

    if (typeof body === 'object' && body !== null) {
      const { failureReason, message } = body as {
        failureReason?: unknown;
        message?: unknown;
      };

      if (typeof failureReason === 'string' && failureReason) {
        return failureReason;
      }

      if (typeof message === 'string' && message) {
        return message;
      }
    }

    return error.message;
  }

  return error instanceof Error ? error.message : undefined;
}
